import { TextField } from '@mui/material';
import type { TextFieldProps } from '@mui/material';
import { useIntl } from 'react-intl';
import { useMemo, useRef, useState } from 'react';
import { useIsScreenSmallerThanBreakpoint } from '@/muiTheme';
import { useRunAfterUpdate } from '@/utils/useRunAfterUpdate';

export default function MoneyTextField({
    maxDecimals = 0,
    onChange,
    value,
    variant,
    ...otherProps
}: Omit<TextFieldProps, 'value' | 'onChange'> & {
    maxDecimals?: number;
    value: number | null;
    onChange(value: number | null): void;
}) {
    const { formatNumber } = useIntl();
    const [formattedValue, setFormattedValue] = useState(
        value != null
            ? formatNumber(value, { maximumFractionDigits: maxDecimals })
            : '',
    );
    const [, setTimestampToForceUpdateHack] = useState(0);
    const isSmallViewport = useIsScreenSmallerThanBreakpoint();
    // Figure out which is the thousands and decimal separators
    const { thousandsSeparator, decimalSeparator } = useMemo(
        () => ({
            decimalSeparator: formatNumber(1.1).replace(/\d/g, ''),
            thousandsSeparator: formatNumber(1000).replace(/\d/g, ''),
        }),
        [formatNumber],
    );
    const wasOnChangeRef = useRef(false);
    const wasOnChange = wasOnChangeRef.current;
    wasOnChangeRef.current = false;

    const runAfterUpdate = useRunAfterUpdate();

    return (
        <TextField
            fullWidth={isSmallViewport}
            value={
                wasOnChange
                    ? formattedValue
                    : value != null
                      ? formatNumber(value, {
                            maximumFractionDigits: maxDecimals,
                        })
                      : ''
            }
            onKeyPress={(event) => {
                const { key } = event;

                if (
                    key !== parseInt(key).toString() &&
                    (maxDecimals === 0 ||
                        (key !== decimalSeparator && key !== '.')) // See comment in onChange about the dot decimal separator
                ) {
                    event.preventDefault();
                }
            }}
            onChange={(event) => {
                wasOnChangeRef.current = true;

                const input = event.currentTarget;
                // Since english numbers are quite common, using dot as decimal separator,
                // unless the thousands separator is a dot, we can make life easier for
                // people that use dot instead of comma's, even though their country says
                // that comma is the decimal separator.
                const inputValue =
                    thousandsSeparator === '.'
                        ? input.value
                        : input.value.replaceAll('.', decimalSeparator);

                const normalizedInputValue = inputValue
                    .replaceAll(thousandsSeparator, '')
                    // We only need the first decimal separator. You never know what people copy and paste
                    .replace(decimalSeparator, '@')
                    .replaceAll(decimalSeparator, '')
                    .replace('@', '.')
                    // trim trailing zeroes if it is a decimal number
                    .replace(/(\.[0-9]*?)[0]*$/, '$1')
                    // trim trailing dot
                    .replace(/\.$/, '');

                const minDecimals = Math.min(
                    maxDecimals,
                    (inputValue.split(decimalSeparator)[1] ?? '').length,
                );

                const newValue = parseFloat(normalizedInputValue);

                if (value !== newValue) {
                    onChange(Number.isNaN(newValue) ? null : newValue);
                }

                const newFormattedValue = Number.isNaN(newValue)
                    ? inputValue
                    : formatNumber(newValue, {
                          style: 'decimal',
                          minimumFractionDigits: minDecimals,
                          maximumFractionDigits: maxDecimals,
                      }) +
                      (inputValue[inputValue.length - 1] === decimalSeparator
                          ? decimalSeparator
                          : '');

                if (formattedValue === newFormattedValue) {
                    // Hack to allow deleting a space character (should just move cursor to the left)
                    // or simply typing zeroes without a jumping cursor
                    setTimestampToForceUpdateHack(Date.now());
                } else {
                    setFormattedValue(newFormattedValue);
                }

                let cursor = input.selectionStart;
                const formattedTextBeforeCursor = newFormattedValue.substring(
                    0,
                    cursor,
                );
                const textBeforeCursor = inputValue.substring(0, cursor);

                if (formattedTextBeforeCursor !== textBeforeCursor) {
                    // Not sure why this work, there must be some cases where
                    // this fails / Marcus
                    cursor += newFormattedValue.length - inputValue.length;
                }

                runAfterUpdate(() => {
                    input.selectionStart = cursor;
                    input.selectionEnd = cursor;
                });
            }}
            variant={variant}
            inputProps={{
                'data-lpignore': 'true',
            }}
            {...otherProps}
        />
    );
}
